import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import BrandMark from "../components/BrandMark";
import {
  ArrowRight, Check, Sparkles, TrendingUp, Bot, BarChart3, PlayCircle,
  Briefcase, Bell, Star, Zap, Database, Smartphone, ShieldCheck,
  Twitter, MessageCircle, ArrowUpRight,
} from "lucide-react";

/* ------------------------- utils ------------------------- */
function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const targetMs = useMemo(() => {
    if (!target) return null;
    const t = new Date(target).getTime();
    return Number.isFinite(t) ? t : null;
  }, [target]);
  if (!targetMs) return { d: 0, h: 0, m: 0, s: 0, done: false };
  const diff = Math.max(0, targetMs - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const secs = Math.floor((diff % 60_000) / 1000);
  return { d: days, h: hours, m: mins, s: secs, done: diff <= 0 };
}

function readUtm() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || null,
    utm_medium: p.get("utm_medium") || null,
    utm_campaign: p.get("utm_campaign") || null,
    utm_content: p.get("utm_content") || null,
    ref_code: p.get("ref") || null,
  };
}

/* ------------------------- countdown tile ------------------------- */
function TimeCell({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex-1 min-w-[68px] sm:min-w-[92px] rounded-2xl border border-white/10 bg-white/[0.02] px-3 py-4 sm:px-5 sm:py-6 text-center"
      style={{ backdropFilter: "blur(20px)" }}
    >
      <div className="font-mono text-4xl sm:text-6xl font-semibold tracking-tight text-white tabular-nums">
        {String(value).padStart(2, "0")}
      </div>
      <div className="mt-1 text-[10px] sm:text-[11px] font-mono tracking-[0.25em] uppercase text-white/40">
        {label}
      </div>
    </div>
  );
}

/* ------------------------- header ------------------------- */
function Nav() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/50 border-b border-white/[0.06]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <BrandMark size={30} />
        <div className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/40">
          Launching soon
        </div>
      </div>
    </header>
  );
}

/* ------------------------- feature cards ------------------------- */
const FEATURES = [
  { icon: TrendingUp, title: "Live Market Scanner",
    desc: "Real-time institutional-grade scanner surfacing high-probability setups in seconds." },
  { icon: Bot, title: "AI Trade Analysis",
    desc: "Ask a professional AI assistant to review any setup, ticker, or market condition." },
  { icon: BarChart3, title: "Advanced Backtester",
    desc: "Historical strategy testing with Monte Carlo and walk-forward validation." },
  { icon: PlayCircle, title: "Trade Replay",
    desc: "Rewind any session, tick by tick — study execution like a pro." },
  { icon: Briefcase, title: "Portfolio Testing",
    desc: "Test a whole strategy across your portfolio, not just a single ticker." },
  { icon: Bell, title: "Custom Alerts",
    desc: "Instant push, email, and SMS alerts on the exact conditions you care about." },
  { icon: Star, title: "Unlimited Watchlists",
    desc: "Organize your universe of setups across as many lists as you want." },
  { icon: MessageCircle, title: "Community",
    desc: "A real trader community — share, fork, and follow winning scan setups." },
  { icon: Zap, title: "Lightning Fast Scanner",
    desc: "Purpose-built infrastructure. Millisecond response on every request." },
  { icon: Database, title: "Institutional Data",
    desc: "Live tick-level market data — the same feed used by pro desks." },
  { icon: Smartphone, title: "Cross Platform",
    desc: "Works flawlessly on desktop, tablet, and phone — no app store needed." },
  { icon: ShieldCheck, title: "Enterprise-Grade Security",
    desc: "Encrypted at rest & in transit. Independent audits. SOC-friendly stack." },
];

function FeatureCard({ icon: Icon, title, desc, delay = 0 }) {
  return (
    <div
      className="group relative rounded-2xl border border-white/[0.06] bg-gradient-to-b from-white/[0.03] to-transparent p-5 sm:p-6 transition-all duration-500 hover:border-white/20 hover:-translate-y-0.5"
      style={{ animation: `fadeUp 0.7s ${delay}s both` }}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 border border-white/10 bg-white/[0.04] group-hover:bg-white/[0.08] transition-colors">
        <Icon className="w-[18px] h-[18px] text-white" strokeWidth={1.5} />
      </div>
      <div className="text-white font-semibold text-[15px] tracking-tight">{title}</div>
      <div className="text-white/50 text-[13px] leading-relaxed mt-1.5">{desc}</div>
    </div>
  );
}

/* ------------------------- pricing tiers ------------------------- */
const PLANS = [
  {
    name: "Free", regular: "$0", price: "$0", suffix: "",
    features: ["15 scans / day", "20-stock watchlist", "3 alerts", "Basic indicators", "30-day backtester"],
    cta: "Start free",
    highlight: false,
  },
  {
    name: "Pro", regular: "$40", price: "$20", suffix: "/mo",
    launchLabel: "Launch price",
    features: ["Unlimited scans", "Advanced filters", "Priority alerts", "Faster refresh", "Everything in Free"],
    cta: "Save 50%",
    highlight: true,
  },
  {
    name: "Executive", regular: "$100", price: "$50", suffix: "/mo",
    launchLabel: "Launch price",
    features: [
      "Everything in Pro", "Unlimited watchlists", "Professional Backtester",
      "Monte Carlo Simulation", "Walk Forward Testing", "Trade Replay",
      "Portfolio Testing", "Unlimited Strategies", "AI Trading Assistant",
      "SMS Alerts", "Exports", "Executive Badge",
    ],
    cta: "Founding member",
    highlight: false,
    executive: true,
  },
];

function PlanCard({ plan }) {
  return (
    <div
      data-testid={`plan-${plan.name.toLowerCase()}`}
      className={`relative rounded-2xl p-6 sm:p-7 flex flex-col ${
        plan.highlight
          ? "border-2 border-white bg-white/[0.02]"
          : "border border-white/10 bg-white/[0.015]"
      }`}
    >
      {plan.highlight && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-mono tracking-[0.2em] uppercase px-2.5 py-1 bg-white text-black rounded-full">
          Most popular
        </div>
      )}
      {plan.executive && (
        <div className="absolute -top-2.5 right-4 text-[10px] font-mono tracking-[0.2em] uppercase px-2.5 py-1 bg-[#00ff9c] text-black rounded-full">
          Executive
        </div>
      )}
      <div className="text-white/50 text-[11px] font-mono uppercase tracking-[0.2em]">{plan.name}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="text-4xl sm:text-5xl text-white font-semibold tracking-tight">{plan.price}</div>
        {plan.suffix && <div className="text-white/40 text-sm">{plan.suffix}</div>}
      </div>
      {plan.launchLabel && (
        <div className="mt-1 flex items-center gap-2 text-[11px]">
          <span className="line-through text-white/30 font-mono">{plan.regular}/mo</span>
          <span className="font-mono uppercase tracking-[0.15em] text-[#00ff9c]">{plan.launchLabel}</span>
        </div>
      )}
      <ul className="mt-6 space-y-2.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[13px] text-white/75">
            <Check className="w-4 h-4 shrink-0 mt-0.5 text-[#00ff9c]" strokeWidth={2.2} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 text-[11px] font-mono tracking-[0.18em] uppercase text-white/40">
        Founding member pricing
      </div>
    </div>
  );
}

/* ------------------------- waitlist form ------------------------- */
function WaitlistForm({ counter }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(true);
  const [smsConsent, setSmsConsent] = useState(false);
  const honeyRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState({ kind: "idle", msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setState({ kind: "err", msg: "Please agree to receive launch updates." });
      return;
    }
    setBusy(true);
    try {
      const utm = readUtm();
      const { data } = await api.post("/api/launch/waitlist", {
        name, email, phone: phone || null,
        email_consent: consent,
        sms_consent: !!phone && smsConsent,
        company_website: honeyRef.current?.value || "",
        ...utm,
        landing_page: window.location.href,
      });
      if (!data.ok) {
        setState({ kind: "err", msg: data.error || "Signup failed. Please try again." });
      } else if (data.duplicate) {
        setState({ kind: "ok", msg: data.message || "You're already on the FlowFinder waitlist." });
      } else if (data.silently_dropped) {
        setState({ kind: "ok", msg: "You're in! We'll notify you the second FlowFinder launches." });
      } else {
        setState({
          kind: "ok",
          msg: "You're in! Check your inbox to confirm your email — we'll notify you the second FlowFinder launches.",
        });
        setName(""); setEmail(""); setPhone("");
      }
    } catch (err) {
      setState({ kind: "err", msg: err?.response?.data?.detail || err.message || "Signup failed." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="waitlist-form" className="w-full max-w-xl mx-auto space-y-3">
      {/* honeypot */}
      <input
        ref={honeyRef}
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        style={{ position: "absolute", left: "-9999px", top: "-9999px", opacity: 0, pointerEvents: "none" }}
        aria-hidden="true"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          data-testid="waitlist-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          required
          className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3.5 text-[14px] text-white placeholder-white/30 focus:outline-none focus:border-white/40 transition"
        />
        <input
          data-testid="waitlist-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          required
          className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3.5 text-[14px] text-white placeholder-white/30 focus:outline-none focus:border-white/40 transition"
        />
      </div>
      <input
        data-testid="waitlist-phone"
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="Phone (optional — for SMS at launch)"
        className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3.5 text-[14px] text-white placeholder-white/30 focus:outline-none focus:border-white/40 transition"
      />
      <label className="flex items-start gap-2.5 text-[12px] text-white/60 cursor-pointer">
        <input
          data-testid="waitlist-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-[#00ff9c]"
        />
        <span>I agree to receive launch updates.</span>
      </label>
      {phone && (
        <label className="flex items-start gap-2.5 text-[12px] text-white/60 cursor-pointer">
          <input
            data-testid="waitlist-sms-consent"
            type="checkbox"
            checked={smsConsent}
            onChange={(e) => setSmsConsent(e.target.checked)}
            className="mt-0.5 accent-[#00ff9c]"
          />
          <span>Text me a launch SMS. Msg &amp; data rates may apply. Reply STOP anytime.</span>
        </label>
      )}
      <button
        data-testid="waitlist-submit"
        disabled={busy}
        type="submit"
        className="group w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black font-semibold text-[14px] px-6 py-4 tracking-tight transition-all hover:bg-white/90 disabled:opacity-60"
      >
        {busy ? "Joining…" : "Join Waitlist"}
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.4} />
      </button>

      {state.kind === "ok" && (
        <div data-testid="waitlist-success" className="text-[13px] text-[#00ff9c] leading-relaxed pt-1">
          {state.msg}
        </div>
      )}
      {state.kind === "err" && (
        <div data-testid="waitlist-error" className="text-[13px] text-red-400 leading-relaxed pt-1">
          {state.msg}
        </div>
      )}

      <div className="pt-2 text-center text-[11px] text-white/40">
        🎁 Waitlist members receive <span className="text-white/70">3 FREE days of Pro at launch.</span>
      </div>
      {counter?.revealed && (
        <div
          data-testid="waitlist-count"
          className="pt-1 text-center text-[12px] font-mono tracking-[0.15em] uppercase text-white/45"
        >
          {counter.count.toLocaleString()} traders have joined the FlowFinder waitlist
        </div>
      )}
    </form>
  );
}

/* ------------------------- main page ------------------------- */
export default function ComingSoon({ launchAt, counter, onLaunched }) {
  const cd = useCountdown(launchAt);
  const doneRef = useRef(false);

  // When countdown hits zero, ping backend once so it can auto-launch.
  useEffect(() => {
    if (cd.done && !doneRef.current) {
      doneRef.current = true;
      (async () => {
        try {
          const { data } = await api.get("/api/launch/config");
          if (data.auto_launched && typeof onLaunched === "function") {
            onLaunched();
          }
        } catch (e) { /* no-op */ }
      })();
    }
  }, [cd.done, onLaunched]);

  return (
    <div className="min-h-screen bg-black text-white antialiased overflow-x-hidden" data-testid="coming-soon-page">
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px);} to { opacity:1; transform: none; } }
        @keyframes pulseSlow { 0%,100% { opacity:.55 } 50% { opacity:.85 } }
      `}</style>

      {/* ambient gradient orbs */}
      <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-30"
             style={{ background: "radial-gradient(circle,#00ff9c 0%,transparent 60%)", animation: "pulseSlow 8s ease-in-out infinite" }} />
        <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full opacity-20"
             style={{ background: "radial-gradient(circle,#7aa2ff 0%,transparent 60%)", animation: "pulseSlow 10s ease-in-out infinite" }} />
        <div className="absolute inset-0"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.02) 1px,transparent 1px), linear-gradient(90deg,rgba(255,255,255,0.02) 1px,transparent 1px)", backgroundSize: "48px 48px" }} />
      </div>

      <Nav />

      {/* HERO */}
      <section className="relative">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-14 sm:pt-24 pb-16 sm:pb-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-mono tracking-[0.18em] uppercase text-white/60"
               style={{ animation: "fadeUp 0.6s 0s both" }}>
            <Sparkles className="w-3 h-3 text-[#00ff9c]" /> Early access is opening
          </div>
          <h1 className="mt-6 text-white font-semibold tracking-tight leading-[1.05] text-4xl sm:text-6xl lg:text-7xl"
              style={{ animation: "fadeUp 0.7s 0.1s both" }}>
            Find Your Next Trade<br />
            <span className="text-white/50">Before Everyone Else.</span>
          </h1>
          <p className="mt-6 max-w-[720px] mx-auto text-white/55 text-[15px] sm:text-lg leading-relaxed"
             style={{ animation: "fadeUp 0.7s 0.2s both" }}>
            FlowFinder scans the market in real time to uncover high-probability trading opportunities
            using AI insights, institutional-grade scanners, customizable alerts, advanced backtesting,
            and professional trading tools.
          </p>

          {/* countdown */}
          {!cd.done ? (
            <div
              data-testid="countdown"
              className="mt-10 sm:mt-12 max-w-[560px] mx-auto flex items-stretch gap-2 sm:gap-3"
              style={{ animation: "fadeUp 0.8s 0.3s both" }}
            >
              <TimeCell label="Days" value={cd.d} testid="cd-days" />
              <TimeCell label="Hours" value={cd.h} testid="cd-hours" />
              <TimeCell label="Minutes" value={cd.m} testid="cd-minutes" />
              <TimeCell label="Seconds" value={cd.s} testid="cd-seconds" />
            </div>
          ) : (
            <div
              data-testid="countdown-live"
              className="mt-10 sm:mt-12 inline-flex items-center gap-3 px-6 py-4 rounded-2xl border border-[#00ff9c]/40 bg-[#00ff9c]/[0.08]"
            >
              <span className="w-2 h-2 rounded-full bg-[#00ff9c] animate-pulse" />
              <span className="text-white font-semibold tracking-tight text-lg">FlowFinder Is Live.</span>
            </div>
          )}

          {/* CTA row */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
               style={{ animation: "fadeUp 0.8s 0.4s both" }}>
            <a href="#waitlist" data-testid="cta-join-waitlist"
               className="group inline-flex items-center gap-2 rounded-full bg-white text-black font-semibold text-[14px] px-6 py-3.5 tracking-tight hover:bg-white/90 transition-all">
              Join the Waitlist <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.4} />
            </a>
            <a href="#waitlist" data-testid="cta-notify-me"
               className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.02] text-white font-semibold text-[14px] px-6 py-3.5 tracking-tight hover:bg-white/[0.06] transition">
              Notify Me At Launch
            </a>
          </div>
        </div>

        {/* waitlist */}
        <div id="waitlist" className="max-w-[1200px] mx-auto px-5 sm:px-8 pb-24">
          <div className="max-w-2xl mx-auto rounded-3xl border border-white/10 bg-white/[0.015] p-6 sm:p-10"
               style={{ backdropFilter: "blur(24px)" }}>
            <div className="text-center mb-6">
              <div className="text-[11px] font-mono tracking-[0.25em] uppercase text-white/40">
                Reserve your spot
              </div>
              <h2 className="mt-2 text-2xl sm:text-3xl text-white font-semibold tracking-tight">
                Be first through the door.
              </h2>
              <div className="mt-2 text-white/50 text-[13px]">
                Join traders getting early access. We&apos;ll email you the moment we launch.
              </div>
            </div>
            <WaitlistForm counter={counter} />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative border-t border-white/[0.06]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="text-[11px] font-mono tracking-[0.25em] uppercase text-white/40">Features</div>
          <h2 className="mt-2 text-3xl sm:text-5xl text-white font-semibold tracking-tight leading-tight max-w-3xl">
            Every edge a professional needs, in one platform.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <FeatureCard key={f.title} {...f} delay={0.05 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="relative border-t border-white/[0.06] bg-white/[0.01]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] font-mono tracking-[0.25em] uppercase text-[#00ff9c]">Founding Member Pricing</div>
            <h2 className="mt-2 text-3xl sm:text-5xl text-white font-semibold tracking-tight">
              Lock in 50% off — forever.
            </h2>
            <div className="mt-3 text-white/50 text-[14px]">
              Launch pricing available only to waitlist members. When we go live, prices return to full.
            </div>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANS.map((p) => <PlanCard key={p.name} plan={p} />)}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="relative border-t border-white/[0.06]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[11px] font-mono tracking-[0.25em] uppercase text-white/40">Why FlowFinder</div>
              <h2 className="mt-2 text-3xl sm:text-4xl text-white font-semibold tracking-tight leading-tight">
                Save hours. Miss nothing. Trade with confidence.
              </h2>
            </div>
            <ul className="space-y-4">
              {[
                "Save hours scanning charts.",
                "Never miss quality setups.",
                "Receive alerts the instant conditions trigger.",
                "Backtest before risking a single dollar.",
                "Trade with a real community of professionals.",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/80 text-[15px]">
                  <div className="mt-0.5 w-5 h-5 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#00ff9c]" strokeWidth={2.6} />
                  </div>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF STRIP */}
      {counter?.revealed && (
        <section className="border-t border-white/[0.06]">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10 flex items-center justify-center gap-3">
            <div className="w-2 h-2 rounded-full bg-[#00ff9c] animate-pulse" />
            <div className="text-white/70 text-[13px] font-mono tracking-[0.15em] uppercase">
              {counter.count.toLocaleString()} traders are on the waitlist
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="border-t border-white/[0.06]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandMark size={24} />
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/30">Launching soon</span>
          </div>
          <div className="flex items-center gap-6 text-[12px] text-white/45">
            <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition">Terms</Link>
            <Link to="/support" className="hover:text-white transition">Contact</Link>
          </div>
          <div className="text-[11px] font-mono text-white/30">
            © {new Date().getFullYear()} FlowFinder
          </div>
        </div>
      </footer>
    </div>
  );
}
